import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Plus, Trash2, ShieldCheck, Loader2, ArrowRight, GripVertical } from 'lucide-react';
import { createWorkSample } from '../services/api';

/* ----------------------------------------------------------------------------
 * Screen A — recruiter authors a proof-of-skill work sample + scoring rubric.
 * POSTs to /api/proof/work-samples. Neutral + signal-green design.
 * -------------------------------------------------------------------------- */

const C = {
  ink: '#0F172A', slate700: '#334155', slate500: '#64748B', slate400: '#94A3B8',
  hairline: '#E2E8F0', page: '#F8FAFC', surface: '#FFFFFF',
  green: '#2563EB', greenInk: '#1D4FD8', greenSoft: '#EFF4FE', greenLine: '#BFDBFE',
  amber: '#B45309', amberSoft: '#FFF8EB', amberLine: '#F4E2BE',
};
const serif = "'Source Serif 4', Georgia, 'Times New Roman', serif";
const slug = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);

const inputStyle = {
  width: '100%', border: `1px solid ${C.hairline}`, borderRadius: 8, padding: '10px 12px',
  fontSize: 14, color: C.ink, background: C.surface, outline: 'none',
};
const labelStyle = { display: 'block', fontSize: 12.5, fontWeight: 600, color: C.slate700, marginBottom: 6 };

function Card({ children, style }) {
  return <div style={{ background: C.surface, border: `1px solid ${C.hairline}`, borderRadius: 12, ...style }}>{children}</div>;
}

const emptyCriterion = () => ({ requirement: '', points_possible: 10, weight: 1, anchors: '' });

function ProofAuthor() {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [roleFamily, setRoleFamily] = useState('');
  const [promptMd, setPromptMd] = useState('');
  const [duration, setDuration] = useState(60);
  const [responseType, setResponseType] = useState('markdown');
  const [criteria, setCriteria] = useState([emptyCriterion()]);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  const totalWeight = criteria.reduce((a, c) => a + (Number(c.weight) || 0), 0);

  const setCrit = (i, patch) => setCriteria((cs) => cs.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const addCrit = () => setCriteria((cs) => [...cs, emptyCriterion()]);
  const removeCrit = (i) => setCriteria((cs) => (cs.length > 1 ? cs.filter((_, j) => j !== i) : cs));

  const handleSave = async () => {
    if (!title.trim()) return toast.error('Add a title');
    if (!promptMd.trim()) return toast.error('Add the task prompt');
    const valid = criteria.filter((c) => c.requirement.trim());
    if (!valid.length) return toast.error('Add at least one rubric criterion');

    const rubric = {
      criteria: valid.map((c) => ({
        id: slug(c.requirement),
        requirement: c.requirement.trim(),
        points_possible: Math.max(1, Number(c.points_possible) || 10),
        weight: Number(c.weight) || 1,
        anchors: c.anchors ? c.anchors.split('\n').map((a) => a.trim()).filter(Boolean) : [],
      })),
    };
    setSaving(true);
    try {
      const res = await createWorkSample({
        title: title.trim(), prompt_md: promptMd.trim(), rubric,
        role_family: roleFamily.trim() || null, duration_minutes: Number(duration) || 60,
        response_type: responseType, scoring_strategy: 'weighted_sum',
      });
      setCreated(res.data);
      toast.success('Work sample published');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not create work sample');
    } finally {
      setSaving(false);
    }
  };

  if (created) {
    return (
      <div style={{ maxWidth: 720, margin: '0 auto', color: C.ink }}>
        <Card style={{ padding: 40, textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: C.greenSoft, border: `1px solid ${C.greenLine}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
            <ShieldCheck size={28} style={{ color: C.green }} />
          </div>
          <h1 style={{ fontFamily: serif, fontSize: 24, fontWeight: 600, margin: '0 0 8px' }}>Work sample published</h1>
          <p style={{ fontSize: 14, color: C.slate500, margin: '0 0 6px' }}>{created.title}</p>
          <p style={{ fontSize: 12.5, color: C.slate400, fontFamily: 'ui-monospace, Menlo, monospace', marginBottom: 22 }}>id: {created.id}</p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button onClick={() => { setCreated(null); setTitle(''); setPromptMd(''); setCriteria([emptyCriterion()]); }}
              style={{ fontSize: 13.5, fontWeight: 600, color: C.ink, background: C.surface, border: `1px solid ${C.hairline}`, borderRadius: 8, padding: '9px 16px', cursor: 'pointer' }}>
              Author another
            </button>
            <button onClick={() => navigate('/proof')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13.5, fontWeight: 600, color: '#fff', background: C.green, border: `1px solid ${C.green}`, borderRadius: 8, padding: '9px 16px', cursor: 'pointer' }}>
              View results <ArrowRight size={15} />
            </button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', color: C.ink }}>
      <div style={{ marginBottom: 22 }}>
        <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.green, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.green }} /> Proof of skill · author
        </div>
        <h1 style={{ fontFamily: serif, fontSize: 28, fontWeight: 600, margin: '12px 0 4px' }}>New work sample</h1>
        <p style={{ fontSize: 14, color: C.slate500, margin: 0 }}>Define a real task and a scoring rubric. The grader scores candidates against it, blind and auditable.</p>
      </div>

      {/* details */}
      <Card style={{ padding: 22, marginBottom: 18 }}>
        <h3 style={{ fontFamily: serif, fontSize: 16, fontWeight: 600, margin: '0 0 16px' }}>Task</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14, marginBottom: 14 }}>
          <div>
            <label style={labelStyle}>Title</label>
            <input style={inputStyle} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Design an API rate limiter" />
          </div>
          <div>
            <label style={labelStyle}>Role family</label>
            <input style={inputStyle} value={roleFamily} onChange={(e) => setRoleFamily(e.target.value)} placeholder="Backend engineering" />
          </div>
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Prompt</label>
          <textarea style={{ ...inputStyle, minHeight: 130, resize: 'vertical', fontFamily: 'ui-monospace, Menlo, monospace', lineHeight: 1.6 }}
            value={promptMd} onChange={(e) => setPromptMd(e.target.value)}
            placeholder="Describe the task. Explain what to deliver, any constraints, and what 'done' looks like." />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div>
            <label style={labelStyle}>Time limit (minutes)</label>
            <input type="number" min={5} style={inputStyle} value={duration} onChange={(e) => setDuration(e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Response type</label>
            <select style={inputStyle} value={responseType} onChange={(e) => setResponseType(e.target.value)}>
              <option value="markdown">Written / markdown</option>
              <option value="code">Code</option>
            </select>
          </div>
        </div>
      </Card>

      {/* rubric */}
      <Card style={{ padding: 22, marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <h3 style={{ fontFamily: serif, fontSize: 16, fontWeight: 600, margin: 0 }}>Scoring rubric</h3>
          <span style={{ fontSize: 12.5, color: totalWeight > 0 ? C.slate500 : C.amber }}>
            {criteria.filter((c) => c.requirement.trim()).length} criteria · total weight {Math.round(totalWeight * 100) / 100}
          </span>
        </div>
        <p style={{ fontSize: 12.5, color: C.slate500, margin: '0 0 16px' }}>The model awards per-criterion points only; the 0–100 score is computed in code as a weighted sum. Negative weights act as penalties.</p>

        {criteria.map((c, i) => (
          <div key={i} style={{ border: `1px solid ${C.hairline}`, borderRadius: 10, padding: 14, marginBottom: 12, background: C.page }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <GripVertical size={16} style={{ color: C.slate400, marginTop: 9, flex: 'none' }} />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 96px 96px', gap: 10, marginBottom: 10 }}>
                  <div>
                    <label style={labelStyle}>Requirement</label>
                    <input style={inputStyle} value={c.requirement} onChange={(e) => setCrit(i, { requirement: e.target.value })} placeholder="What good looks like for this criterion" />
                  </div>
                  <div>
                    <label style={labelStyle}>Points</label>
                    <input type="number" min={1} style={inputStyle} value={c.points_possible} onChange={(e) => setCrit(i, { points_possible: e.target.value })} />
                  </div>
                  <div>
                    <label style={labelStyle}>Weight</label>
                    <input type="number" step="0.1" style={inputStyle} value={c.weight} onChange={(e) => setCrit(i, { weight: e.target.value })} />
                  </div>
                </div>
                <label style={labelStyle}>Anchors <span style={{ color: C.slate400, fontWeight: 400 }}>(optional, one per line: e.g. "10 = …", "5 = …", "0 = …")</span></label>
                <textarea style={{ ...inputStyle, minHeight: 56, resize: 'vertical', fontSize: 13 }} value={c.anchors} onChange={(e) => setCrit(i, { anchors: e.target.value })} placeholder={'10 = fully correct and robust\n5 = partially correct\n0 = incorrect'} />
              </div>
              <button onClick={() => removeCrit(i)} title="Remove" style={{ marginTop: 26, color: C.slate400, background: 'none', border: 'none', cursor: 'pointer', flex: 'none' }}>
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
        <button onClick={addCrit} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13.5, fontWeight: 600, color: C.greenInk, background: C.greenSoft, border: `1px solid ${C.greenLine}`, borderRadius: 8, padding: '8px 14px', cursor: 'pointer' }}>
          <Plus size={15} /> Add criterion
        </button>
      </Card>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginBottom: 32 }}>
        <button onClick={handleSave} disabled={saving}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600, color: '#fff', background: saving ? C.slate400 : C.green, border: `1px solid ${saving ? C.slate400 : C.green}`, borderRadius: 8, padding: '11px 22px', cursor: saving ? 'default' : 'pointer' }}>
          {saving ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
          Publish work sample
        </button>
      </div>

      <style>{`.animate-spin{animation:paspin 1s linear infinite}@keyframes paspin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

export default ProofAuthor;
